/**
 * Builds the footer save actions for the Binom Standard tab.
 *  - Inline rotation (`inline:<campaignId>`): one button → PUT campaign.
 *  - Shared rotation (numeric flowId): three buttons → Save rotation /
 *    Save campaign (detaches) / Save both (preserves shared link).
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  BinomFlowUpdateBody,
  BinomUpdateTarget,
} from '@/hooks/binom/useBinomFlow';
import type { FlowOptimizerSaveAction } from '@/features/flow-optimizer/FlowOptimizerDrawerFooterContext';

const HELP = {
  rotation:
    "PUT /api/rotation/:id with the merged paths / landers / offers. The SHARED rotation gets updated — all campaigns linked to it pick up the change. Campaign metadata is NOT sent.",
  campaignShared:
    "PUT /api/campaign/:id with paths embedded as customRotation + the metadata patch. DETACHES this campaign from the shared rotation into a private snapshot — other campaigns are unaffected. Mirrors the Binom panel Save button.",
  campaignInline: 'Saves the campaign with the merged inline rotation embedded.',
  both: 'PUT /api/rotation/:id (shared update) then PUT /api/campaign/:id with just the metadata. KEEPS the shared rotation link.',
};

export interface BuildActionsParams {
  flowId: string;
  campaignId: string | undefined;
  baseBody: BinomFlowUpdateBody;
  disabled: boolean;
  save: (body: BinomFlowUpdateBody) => Promise<void>;
}

export function buildBinomSaveActions(
  params: BuildActionsParams,
): FlowOptimizerSaveAction[] {
  const { flowId, campaignId, baseBody, disabled, save } = params;
  const isInline = flowId.startsWith('inline:');
  if (isInline) {
    return [
      makeAction('save-inline', 'Save', 'primary', disabled, HELP.campaignInline, () =>
        save({ ...baseBody, target: 'campaign' }),
      ),
    ];
  }
  return [
    makeAction(
      'save-rotation',
      'Save to rotation',
      'secondary',
      disabled,
      HELP.rotation,
      () => save({ ...baseBody, target: 'rotation', campaignId }),
    ),
    makeAction(
      'save-campaign',
      'Save to campaign',
      'secondary',
      disabled || !campaignId,
      HELP.campaignShared,
      () => save({ ...baseBody, target: 'campaign', campaignId }),
    ),
    makeAction(
      'save-both',
      'Save to both',
      'primary',
      disabled || !campaignId,
      HELP.both,
      () => save({ ...baseBody, target: 'both', campaignId }),
    ),
  ];
}

function makeAction(
  id: string,
  label: string,
  variant: 'primary' | 'secondary',
  disabled: boolean,
  helpText: string,
  run: () => Promise<void>,
): FlowOptimizerSaveAction {
  return {
    id,
    label,
    variant,
    disabled,
    helpText,
    onClick: run,
  };
}

export function targetConfirmMessage(target: BinomUpdateTarget): string {
  if (target === 'rotation') {
    return 'PUT /api/rotation/:id — update the SHARED rotation? All campaigns using it pick up the change. Campaign metadata is NOT sent.';
  }
  if (target === 'campaign') {
    return 'PUT /api/campaign/:id with paths + metadata — DETACHES this campaign from the shared rotation into a private snapshot. Other campaigns are unaffected. (Mirrors the Binom panel.)';
  }
  return 'PUT /api/rotation/:id then PUT /api/campaign/:id — update the shared rotation AND save the metadata, keeping the link.';
}
